import { Fragment, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TbEye, TbEyeOff, TbInfoCircle, TbAlertCircle } from "react-icons/tb";
import { sanitizeInput, trimInput } from "src/helpers/sanitizeInput";
import Loader from "src/components/Loader/Loader";
import Popup from "src/components/Popup/Popup";
import { Section } from "./SettingsControls";
import { Doctor } from "src/types";
import { apiRequest } from "src/services/apiClient";
import { SPECIALTY_OPTIONS } from "./SpecialtyPicker";
import { useAuth } from "src/context/AuthProvider";
import { useEscKey } from "src/hooks/useEscKey";
import { useFocusTrap } from "src/hooks/useFocusTrap";

const PLAN_ALLOWANCE: Record<number, { doctors: number; staff: number }> = {
    1: { doctors: 1, staff: 0 },
    2: { doctors: 2, staff: 1 },
    3: { doctors: 2, staff: 2 },
    4: { doctors: 2, staff: 2 },
};

const EMPTY_FORM = { salutation: "H", firstName: "", lastName: "", email: "", password: "", specialty: "", userRights: 2 };

type DoctorFormData = Pick<Doctor, "salutation" | "firstName" | "lastName" | "email"> & {
    specialty: string;
    password?: string;
    userRights: number;
};

interface DoctorFormProps {
    f: typeof EMPTY_FORM;
    setF: (v: typeof EMPTY_FORM) => void;
    onSubmit: (e: React.FormEvent) => void;
    onClose: () => void;
    submitting: boolean;
    error: string | null;
    submitLabel: string;
    isEdit: boolean;
    specialties: string[];
    costNote?: string | null;
    // True when this form is editing the praxis's only remaining Admin — the role field
    // locks to Admin so the praxis can never end up with zero Admins through this form
    // (backend enforces the same rule; this just avoids a round-trip to discover it).
    lockAdminRole?: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const SPECIAL_RE = /[!@#$%^&*()\-_=+\[\]{};:'",.<>/?\\|`~]/;

function DoctorForm({ f, setF, onSubmit, onClose, submitting, error, submitLabel, isEdit, specialties, costNote, lockAdminRole }: DoctorFormProps) {
    const { t } = useTranslation();
    const [showPw, setShowPw] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [pwTouched, setPwTouched] = useState(false);

    const USER_ROLES = [
        { value: 1, label: t("roles.medicalAssistant") },
        { value: 2, label: t("roles.doctor") },
        { value: 3, label: t("roles.admin") },
        { value: 5, label: t("roles.accounting") },
    ];

    const SALUTATION_OPTIONS = [
        { value: "H", label: t("form.herr") },
        { value: "F", label: t("form.frau") },
    ];

    const isDoctor = f.userRights === 2 || f.userRights === 3;
    const roleDescKey = `settings.roleDesc.${f.userRights}`;
    const roleDescText = t(roleDescKey);
    const isAccountingRole = f.userRights === 5;

    const emailError = emailTouched && f.email && !EMAIL_RE.test(f.email) ? t("settings.emailInvalid") : null;
    const pwError = pwTouched && f.password
        ? f.password.length < 8
            ? t("settings.passwordTooShort")
            : !SPECIAL_RE.test(f.password)
                ? t("settings.passwordNeedsSpecial")
                : null
        : null;

    return (
        <form className="create-modal__form" onSubmit={onSubmit}>
            <div className="create-modal__row">
                <div className="create-modal__field">
                    <label htmlFor="team-salutation">{t("form.salutationRequired")}</label>
                    <select id="team-salutation" name="salutation" value={f.salutation} onChange={(e) => setF({ ...f, salutation: e.target.value })}>
                        {SALUTATION_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div className="create-modal__field">
                    <label htmlFor="team-role">{t("settings.roleRequired")}</label>
                    <select
                        id="team-role"
                        name="userRights"
                        value={f.userRights}
                        disabled={lockAdminRole}
                        onChange={(e) => {
                            const nextRole = Number(e.target.value);
                            const stillNeedsSpecialty = nextRole === 2 || nextRole === 3;
                            // Only clear specialty when the new role wouldn't even show the
                            // field — switching Doctor <-> Admin (both show it) must keep
                            // whatever was already on file instead of forcing a re-entry.
                            setF({ ...f, userRights: nextRole, specialty: stillNeedsSpecialty ? f.specialty : "" });
                        }}
                    >
                        {USER_ROLES.map((r) => (
                            <option key={r.value} value={r.value} disabled={lockAdminRole && r.value !== 3}>{r.label}</option>
                        ))}
                    </select>
                    {lockAdminRole && <span className="field-hint">{t("settings.roleChangeLastAdmin")}</span>}
                </div>
            </div>
            <div className={`role-desc-hint${isAccountingRole ? " role-desc-hint--warn" : ""}`}>
                <span className="role-desc-hint__icon">{isAccountingRole ? "🔒" : "ℹ"}</span>
                <span className="role-desc-hint__text">{roleDescText}</span>
            </div>
            <div className="create-modal__row">
                <div className="create-modal__field">
                    <label htmlFor="team-firstname">{t("form.firstName")}</label>
                    <input id="team-firstname" name="firstName" value={f.firstName} onChange={(e) => setF({ ...f, firstName: sanitizeInput(e.target.value) })} onBlur={(e) => setF({ ...f, firstName: trimInput(e.target.value) })} />
                </div>
                <div className="create-modal__field">
                    <label htmlFor="team-lastname">{t("form.lastName")} *</label>
                    <input id="team-lastname" name="lastName" value={f.lastName} onChange={(e) => setF({ ...f, lastName: sanitizeInput(e.target.value) })} onBlur={(e) => setF({ ...f, lastName: trimInput(e.target.value) })} />
                </div>
            </div>
            <div className="create-modal__row">
                <div className="create-modal__field">
                    <label htmlFor="team-email">{t("settings.emailRequired")}</label>
                    <input
                        id="team-email"
                        name="email"
                        type="email"
                        autoComplete="new-password"
                        value={f.email}
                        className={emailError ? "input--error" : ""}
                        onChange={(e) => setF({ ...f, email: sanitizeInput(e.target.value) })}
                        onBlur={(e) => { setEmailTouched(true); setF({ ...f, email: trimInput(e.target.value) }); }}
                    />
                    {emailError && <span className="field-error">{emailError}</span>}
                </div>
                <div className="create-modal__field">
                    <label htmlFor="team-password">{t("settings.passwordLabel")}{isEdit ? ` ${t("settings.passwordKeep")}` : " *"}</label>
                    <div className="pw-wrap">
                        <input
                            id="team-password"
                            name="password"
                            type={showPw ? "text" : "password"}
                            autoComplete="new-password"
                            value={f.password}
                            className={pwError ? "input--error" : ""}
                            onChange={(e) => setF({ ...f, password: e.target.value })}
                            onBlur={() => setPwTouched(true)}
                        />
                        <button type="button" className="pw-eye" onClick={() => setShowPw((v) => !v)} tabIndex={-1}>
                            {showPw ? <TbEyeOff /> : <TbEye />}
                        </button>
                    </div>
                    {pwError && <span className="field-error">{pwError}</span>}
                </div>
            </div>
            {isDoctor && (
                <div className="create-modal__row">
                    <div className="create-modal__field">
                        <label htmlFor="team-specialty">{t("settings.specialty")}</label>
                        <select id="team-specialty" name="specialty" value={f.specialty} onChange={(e) => setF({ ...f, specialty: e.target.value })}>
                            <option value="">— {t("settings.selectSpecialty")} —</option>
                            {[...specialties].sort((a, b) => a.localeCompare(b, "de")).map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
            {error && <p className="create-modal__error">{error}</p>}
            {costNote && (
                <div className="settings-info-banner settings-info-banner--warn">
                    <TbAlertCircle className="settings-info-banner__icon" />
                    <span>{costNote}</span>
                </div>
            )}
            <div className="create-modal__actions">
                <button type="button" className="create-modal__btn create-modal__btn--cancel" onClick={onClose}>{t("form.cancel")}</button>
                <button type="submit" className="create-modal__btn create-modal__btn--submit" disabled={submitting}>{submitting ? t("settings.saving") : submitLabel}</button>
            </div>
        </form>
    );
}

interface TeamSettingsProps {
    doctors: Doctor[] | null;
    loading: boolean;
    currentDoctorId: string;
    onRoleChange: (id: string, userRights: number) => void;
    onPermChange: (id: string, perms: { permViewAllInvoices: boolean; permEdit: boolean; permRecipients: boolean; permPatientsDelete: boolean; permSettings: boolean; permBank: boolean; permBankReconcile: boolean; permReports: boolean; permDunning: boolean; permDatev: boolean; permCreateForOthers: boolean; permTagsGroupsView: boolean; permTagsGroupsEdit: boolean; permDashboardTasks: boolean; permPvsSavings: boolean; permBankDashboard: boolean; permBankDoctorScope: string; permCardQr: boolean }) => void;
    onDelete: (id: string, mode: "immediate" | "endOfMonth") => Promise<void>;
    onCancelDeletion: (id: string) => void;
    onAdd: (data: DoctorFormData) => Promise<void>;
    onEdit: (id: string, data: DoctorFormData) => Promise<void>;
    /** When given, the Save button renders here (the shared, fixed-position breadcrumb
     *  action slot other Settings subpages use) instead of inline below the matrix. Left
     *  undefined in standalone/test rendering, where it falls back to the inline button. */
    saveButtonPortalTarget?: HTMLElement | null;
}

export function TeamSettings({
    doctors,
    loading,
    currentDoctorId,
    onRoleChange,
    onPermChange,
    onDelete,
    onCancelDeletion,
    onAdd,
    onEdit,
    saveButtonPortalTarget,
}: TeamSettingsProps) {
    const { t } = useTranslation();
    const { user } = useAuth();
    // Only an Admin may edit ANYONE's permissions (matches the backend's requireAdmin
    // guard on PUT doctor-role/doctor-permissions) — the matrix must never render another
    // row's checkboxes as interactive for a non-admin viewer, regardless of whose row it
    // is, including the Administrator's own row.
    const isViewerAdmin = user?.doctor?.userRights === 3;
    // Backstops the backend's own last-admin check (upsertDoctor) — lets the role select
    // lock itself in the UI instead of only failing after a round-trip.
    const adminCount = doctors?.filter((d) => d.userRights === 3).length ?? 0;
    // Only Doctor/Admin roles can ever own a BanksapiConnection (requireBankManage is
    // Doctor/Admin-only, non-delegable) — the only team members worth offering as a bank
    // permission's doctor-scope target.
    const bankScopeDoctors = doctors?.filter((d) => d.userRights === 2 || d.userRights === 3) ?? [];
    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    const [editDoc, setEditDoc] = useState<any | null>(null);
    const [editForm, setEditForm] = useState(EMPTY_FORM);
    const [editing, setEditing] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Doctor | null>(null);
    const [deleteMode, setDeleteMode] = useState<"immediate" | "endOfMonth">("immediate");
    const [deleteAck, setDeleteAck] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [localPerms, setLocalPerms] = useState<Map<string, { viewAllInvoices: boolean; edit: boolean; recipients: boolean; recipientsDelete: boolean; settings: boolean; bank: boolean; bankReconcile: boolean; reports: boolean; dunning: boolean; datev: boolean; createForOthers: boolean; tagsGroupsView: boolean; tagsGroupsEdit: boolean; dashboardTasks: boolean; pvsSavings: boolean; bankDashboard: boolean; bankDoctorScope: string; cardQr: boolean }>>(new Map());
    const [permsSaving, setPermsSaving] = useState(false);
    const specialties = SPECIALTY_OPTIONS;

    const addDialogRef = useRef<HTMLDivElement>(null);
    const editDialogRef = useRef<HTMLDivElement>(null);
    const addTitleId = useRef(`team-add-title-${Math.random().toString(36).slice(2)}`).current;
    const editTitleId = useRef(`team-edit-title-${Math.random().toString(36).slice(2)}`).current;

    const USER_ROLES = [
        { value: 1, label: t("roles.medicalAssistant") },
        { value: 2, label: t("roles.doctor") },
        { value: 3, label: t("roles.admin") },
        { value: 5, label: t("roles.accounting") },
    ];

    // Human-readable label per permission, reused for both the matrix row header and each
    // checkbox's aria-label — a screen reader should hear "Rechnungen bearbeiten" like a
    // sighted user reads it, not the raw field key ("edit").
    // "bankReconcile" is deliberately not a row of its own — like "edit" (viewAllInvoices) and
    // "recipientsDelete" (recipients), it's a hidden second field folded into the "bank" row's
    // own None/View/Edit segmented control below, not an independently listed permission.
    const PERM_ROWS = ["viewAllInvoices", "createForOthers", "recipients", "settings", "bank", "reports", "dunning", "datev", "tagsGroupsView", "tagsGroupsEdit", "dashboardTasks", "pvsSavings", "bankDashboard", "cardQr"] as const;
    const permLabel: Record<(typeof PERM_ROWS)[number], string> = {
        viewAllInvoices: t("settings.rolesTable.viewAllInvoices"),
        createForOthers: t("settings.rolesTable.createForOthers"),
        recipients:     t("settings.rolesTable.manageRecipients"),
        settings:       t("settings.rolesTable.manageSettings"),
        bank:           t("settings.rolesTable.bankPayments"),
        reports:        t("settings.rolesTable.reports"),
        dunning:        t("settings.rolesTable.dunning"),
        datev:          t("settings.rolesTable.datev"),
        tagsGroupsView: t("settings.rolesTable.viewTagsGroups"),
        tagsGroupsEdit: t("settings.rolesTable.editTagsGroups"),
        dashboardTasks: t("settings.rolesTable.dashboardTasks"),
        pvsSavings:     t("settings.rolesTable.pvsSavings"),
        bankDashboard:  t("settings.rolesTable.bankDashboard"),
        cardQr:         t("settings.rolesTable.cardQr"),
    };

    // Groups the flat permission list into labeled bands so the matrix reads as related
    // decisions instead of one undifferentiated list. Dashboard tasks (and the PVS savings
    // figure alongside it) gets its own group rather than joining groupAdmin — unlike
    // reports/dunning/datev neither is an administrative action and neither should get reset
    // when Settings access is revoked. Bank gets its own group too, same reasoning as Tags &
    // Groups below — it isn't reset or require-granted alongside Settings (see
    // handlePermChange): a team member can see/reconcile banking without full Settings & Team
    // access, so it doesn't belong visually bundled with the admin-only rows that do require it.
    const PERM_GROUPS: { label: string; perms: (typeof PERM_ROWS)[number][] }[] = [
        { label: t("settings.rolesTable.groupInvoices"), perms: ["viewAllInvoices", "createForOthers", "recipients"] },
        { label: t("settings.rolesTable.groupAdmin"), perms: ["settings", "reports", "dunning", "datev"] },
        { label: t("settings.rolesTable.groupBank"), perms: ["bank", "cardQr"] },
        { label: t("settings.rolesTable.groupTags"), perms: ["tagsGroupsView", "tagsGroupsEdit"] },
        { label: t("settings.rolesTable.groupDashboard"), perms: ["dashboardTasks", "pvsSavings", "bankDashboard"] },
    ];

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.lastName || !form.email || !form.password) {
            setAddError(t("settings.errorAddMember"));
            return;
        }
        if (!EMAIL_RE.test(form.email)) {
            setAddError(t("settings.emailInvalid"));
            return;
        }
        if (form.password.length < 8) {
            setAddError(t("settings.passwordTooShort"));
            return;
        }
        if (!SPECIAL_RE.test(form.password)) {
            setAddError(t("settings.passwordNeedsSpecial"));
            return;
        }
        setAdding(true);
        setAddError(null);
        try {
            await onAdd(form);
            setAddOpen(false);
            setForm(EMPTY_FORM);
        } catch (err: any) {
            setAddError(err?.message ?? t("settings.errorFailedAddDoctor"));
        } finally {
            setAdding(false);
        }
    };

    const handleEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editForm.lastName || !editForm.email) {
            setEditError(t("settings.errorEditMember"));
            return;
        }
        if (!EMAIL_RE.test(editForm.email)) {
            setEditError(t("settings.emailInvalid"));
            return;
        }
        if (editForm.password) {
            if (editForm.password.length < 8) {
                setEditError(t("settings.passwordTooShort"));
                return;
            }
            if (!SPECIAL_RE.test(editForm.password)) {
                setEditError(t("settings.passwordNeedsSpecial"));
                return;
            }
        }
        setEditing(true);
        setEditError(null);
        try {
            await onEdit(editDoc.id, editForm);
            setEditDoc(null);
        } catch (err: any) {
            setEditError(
                err?.message === "LAST_ADMIN"
                    ? t("settings.roleChangeLastAdmin")
                    : err?.message ?? t("settings.errorFailedSave"),
            );
        } finally {
            setEditing(false);
        }
    };

    const openEdit = (doc: Doctor) => {
        setEditDoc(doc);
        setEditForm({
            salutation: doc.salutation ?? "H",
            firstName: doc.firstName ?? "",
            lastName: doc.lastName ?? "",
            email: doc.email ?? "",
            password: "",
            specialty: Array.isArray(doc.specialty) ? (doc.specialty[0] ?? "") : "",
            userRights: doc.userRights ?? 2,
        });
        setEditError(null);
    };

    const closeAdd = () => { setAddOpen(false); setForm(EMPTY_FORM); setAddError(null); };
    const closeEdit = () => { setEditDoc(null); setEditError(null); };

    useEscKey(closeAdd, addOpen);
    useEscKey(closeEdit, !!editDoc);
    useFocusTrap(addOpen, addDialogRef);
    useFocusTrap(!!editDoc, editDialogRef);

    const permsFromRole = (role: number) => ({
        // Matches backend praxisScope(): Accounting sees all invoices by default since
        // that's its job function; Doctor and Admin always see only their own invoices
        // (not a grant — see the matrix's fixed "own" label for those roles);
        // MedicalAssistant needs it explicitly granted via the matrix's None/View/Edit
        // control. Patients have no equivalent row — every role sees the whole praxis's
        // patients unconditionally.
        viewAllInvoices: role === 5,
        // Invoice write access — for Admin/MedicalAssistant/Accounting this is merged
        // into the viewAllInvoices row as a None/View/Edit segmented control (see the
        // matrix JSX) rather than its own row; for Doctor there's no UI control at all
        // (they always can, matching the backend role default below), so this must stay
        // role-accurate even though it's not shown, or saving an unrelated permission for
        // a Doctor would silently overwrite it.
        edit:       role === 2 || role === 3,
        // Manage recipients & patients — a normal, independently togglable permission for
        // every role (not tied to any view scope, since patient visibility itself isn't
        // gated at all).
        recipients: role === 2 || role === 3,
        // Delete patients/recipients — Doctor + Admin by default, matching the backend
        // route guard (requirePatientsDelete). Separate from `recipients` (create/edit)
        // since delete cascades to every doctor's invoices for that patient, not just
        // this one's — but doctors are trusted with it by default too, same as edit.
        recipientsDelete: role === 2 || role === 3,
        settings:   role === 2 || role === 3,
        bank:       role === 2 || role === 3,
        // Same default as bank itself — Doctor/Admin can already reconcile their own
        // transactions day to day, matching requireBankReconcile's backend role default.
        bankReconcile: role === 2 || role === 3,
        reports:    role === 2 || role === 3 || role === 5,
        dunning:    role === 2 || role === 3,
        // DATEV export config is Admin-only by default (matches settingsController's
        // canManageDatev — getting this wrong misdirects the praxis's actual accounting),
        // unlike Bank/Reports/Dunning which default open to Doctor too.
        datev:      role === 3,
        // Create/upload invoices for another doctor — a grant for Doctor/Admin (they otherwise
        // create for themselves, services/invoiceOwner.ts); staff always pick, so it doesn't apply.
        createForOthers: false,
        // Tags/groups default to admin-only, unlike the other features above —
        // there's no broader role default to fall back to, only an explicit grant.
        tagsGroupsView: role === 3,
        tagsGroupsEdit: role === 3,
        // Doctor/Admin already see the dashboard's task list unconditionally (it's not
        // gated by this flag for them) — this only matters for MedicalAssistant/Accounting,
        // who get a stripped-down dashboard by default and need it explicitly granted.
        dashboardTasks: role !== 1 && role !== 5,
        // Admin-only by default, unlike dashboardTasks — the Charges page itself stays
        // Admin-only regardless of this grant, so a permitted non-Admin sees the dashboard
        // figure but can't click through to it. Needs an explicit grant even for Doctor.
        pvsSavings: role === 3,
        // Same default shape as bank itself (view) — whether the Dashboard's compact bank
        // summary card (balance + transaction preview) also shows there, not just on the
        // full Bank page. Doctor/Admin on by default; staff need it explicitly granted.
        bankDashboard: role === 2 || role === 3,
        // Which doctor's connected account this grant covers — "all" (today's merged-across-
        // every-doctor default) unless narrowed to one specific doctor below.
        bankDoctorScope: "all",
        // "Kartenzahlung per QR" on invoices (SumUp) — Doctor/Admin by default, staff at the desk
        // need it granted (backend requireCardQr).
        cardQr: role === 2 || role === 3,
    });

    const getPerms = (doc: Doctor) => {
        if (localPerms.has(doc.id)) return localPerms.get(doc.id)!;
        const base = permsFromRole(doc.userRights ?? 2);
        return {
            viewAllInvoices: doc.permViewAllInvoices ?? base.viewAllInvoices,
            edit:       doc.permEdit       ?? base.edit,
            recipients: doc.permRecipients ?? base.recipients,
            recipientsDelete: doc.permPatientsDelete ?? base.recipientsDelete,
            settings:   doc.permSettings   ?? base.settings,
            bank:       doc.permBank       ?? base.bank,
            bankReconcile: doc.permBankReconcile ?? base.bankReconcile,
            reports:    doc.permReports    ?? base.reports,
            dunning:    doc.permDunning    ?? base.dunning,
            datev:      doc.permDatev      ?? base.datev,
            createForOthers: doc.permCreateForOthers ?? base.createForOthers,
            tagsGroupsView: doc.permTagsGroupsView ?? base.tagsGroupsView,
            tagsGroupsEdit: doc.permTagsGroupsEdit ?? base.tagsGroupsEdit,
            dashboardTasks: doc.permDashboardTasks ?? base.dashboardTasks,
            pvsSavings: doc.permPvsSavings ?? base.pvsSavings,
            bankDashboard: doc.permBankDashboard ?? base.bankDashboard,
            bankDoctorScope: doc.permBankDoctorScope ?? base.bankDoctorScope,
            cardQr: doc.permCardQr ?? base.cardQr,
        };
    };

    const handlePermChange = (doc: Doctor, perm: "viewAllInvoices" | "edit" | "recipients" | "settings" | "bank" | "bankReconcile" | "reports" | "dunning" | "datev" | "createForOthers" | "tagsGroupsView" | "tagsGroupsEdit" | "dashboardTasks" | "pvsSavings" | "bankDashboard" | "cardQr", value: boolean) => {
        const current = getPerms(doc);
        const next = { ...current, [perm]: value };
        // Editing invoices does NOT require viewAll — patchInvoice/deleteInvoice already scope
        // to the caller's own doctorId regardless of permViewAll, so a doctor can be granted
        // edit rights over their own invoices without seeing the whole praxis's invoices too.
        // Reports/Dunning/DATEV are administrative actions gated behind Settings & Team.
        // Bank/BankReconcile are deliberately NOT bundled in here — a team member (e.g. a
        // medical assistant) can be given banking visibility, or the ability to reconcile
        // transactions, on their own without also getting the broader Settings & Team
        // management access, matching requireBank/requireBankReconcile's backend checks, which
        // are independent of permSettings (and of each other).
        if (perm === "settings" && !value) { next.reports = false; next.dunning = false; next.datev = false; }
        if ((perm === "reports" || perm === "dunning" || perm === "datev") && value) next.settings = true;
        // Managing tags/groups requires being able to view them first.
        if (perm === "tagsGroupsView" && !value) next.tagsGroupsEdit = false;
        if (perm === "tagsGroupsEdit" && value) next.tagsGroupsView = true;
        setLocalPerms((prev) => new Map(prev).set(doc.id, next));
    };

    const handleSavePerms = async () => {
        setPermsSaving(true);
        try {
            await Promise.all(
                [...localPerms.entries()].map(([id, p]) =>
                    onPermChange(id, {
                        permViewAllInvoices: p.viewAllInvoices,
                        permEdit:       p.edit,
                        permRecipients: p.recipients,
                        permPatientsDelete: p.recipientsDelete,
                        permSettings:   p.settings,
                        permBank:       p.bank,
                        permBankReconcile: p.bankReconcile,
                        permReports:    p.reports,
                        permDunning:    p.dunning,
                        permDatev:      p.datev,
                        permCreateForOthers: p.createForOthers,
                        permTagsGroupsView: p.tagsGroupsView,
                        permTagsGroupsEdit: p.tagsGroupsEdit,
                        permDashboardTasks: p.dashboardTasks,
                        permPvsSavings: p.pvsSavings,
                        permBankDashboard: p.bankDashboard,
                        permBankDoctorScope: p.bankDoctorScope,
                        permCardQr: p.cardQr,
                    }),
                ),
            );
            setLocalPerms(new Map());
        } finally {
            setPermsSaving(false);
        }
    };

    const saveButton = (
        <button
            className="create-modal__btn create-modal__btn--submit"
            onClick={handleSavePerms}
            disabled={localPerms.size === 0 || permsSaving}
        >
            {permsSaving ? t("settings.saving") : t("settings.saveChangesBtn")}
        </button>
    );

    const plan = user?.praxis?.businessPlan ?? 1;
    const allowance = PLAN_ALLOWANCE[plan] ?? PLAN_ALLOWANCE[1];
    const EXTRA_DOCTOR_FEE = plan === 4 ? 0 : 39;
    const EXTRA_STAFF_FEE = plan === 4 ? 0 : 9.9;
    const DOCTOR_ROLES = [2, 3];
    const STAFF_ROLES = [1, 5];
    const doctorMembers = (doctors ?? []).filter(d => DOCTOR_ROLES.includes(d.userRights ?? 2));
    const staffMembers = (doctors ?? []).filter(d => STAFF_ROLES.includes(d.userRights ?? 2));
    const extraDoctorIds = new Set(doctorMembers.slice(allowance.doctors).map(d => d.id));
    const extraStaffIds = new Set(staffMembers.slice(allowance.staff).map(d => d.id));

    const getAddCostNote = () => {
        const isDocRole = form.userRights === 2 || form.userRights === 3;
        if (isDocRole && doctorMembers.length >= allowance.doctors && EXTRA_DOCTOR_FEE > 0) {
            return t("settings.addMemberCostWarn", { fee: `€${EXTRA_DOCTOR_FEE}`, defaultValue: "Adding this member costs {{fee}}/month (excl. 19% VAT) and is billed in full for the current month, effective immediately. You can remove them again at any time; our Terms of Service (AGB) apply." });
        }
        if (!isDocRole && form.userRights !== 0 && staffMembers.length >= allowance.staff && EXTRA_STAFF_FEE > 0) {
            return t("settings.addMemberCostWarn", { fee: `€${EXTRA_STAFF_FEE.toFixed(2).replace(".", ",")}`, defaultValue: "Adding this member costs {{fee}}/month (excl. 19% VAT) and is billed in full for the current month, effective immediately. You can remove them again at any time; our Terms of Service (AGB) apply." });
        }
        return null;
    };

    const isExtraMember = (target: Doctor) => extraDoctorIds.has(target.id) || extraStaffIds.has(target.id);
    const feeForMember = (target: Doctor) => extraDoctorIds.has(target.id)
        ? `€${EXTRA_DOCTOR_FEE}`
        : `€${EXTRA_STAFF_FEE.toFixed(2).replace(".", ",")}`;

    const openDelete = (doc: Doctor) => {
        setDeleteTarget(doc);
        setDeleteMode("immediate");
        setDeleteAck(false);
        setDeleteError(null);
    };
    const closeDelete = () => {
        setDeleteTarget(null);
        setDeleteAck(false);
        setDeleteError(null);
    };
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await onDelete(deleteTarget.id, deleteMode);
            closeDelete();
        } catch (err: any) {
            setDeleteError(
                err?.message === "LAST_ADMIN"
                    ? t("settings.deleteAccountLastAdmin")
                    : err?.message ?? t("settings.errorFailedSave"),
            );
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Section title={t("settings.teamMembers")} actions={
            <button className="create-modal__btn create-modal__btn--submit" onClick={() => setAddOpen(true)}>
                {t("settings.addMember")}
            </button>
        }>
            <div className="settings-info-banner settings-info-banner--neutral settings-info-banner--row">
                <TbInfoCircle size={16} />
                {t("settings.teamPlanHint", {
                    doctors: allowance.doctors,
                    staff: allowance.staff,
                    defaultValue: "Your plan includes {{doctors}} doctor(s)/admin(s) and {{staff}} staff member(s). Extra doctors/admins cost €39/month, extra staff €9,90/month (excl. 19% VAT).",
                })}
            </div>
            {loading ? (
                <Loader />
            ) : !doctors?.length ? (
                <p className="settings-empty">{t("settings.noTeamMembers")}</p>
            ) : (
                <>
                    {/* Member list */}
                    <table className="dashboard-table">
                        <thead>
                            <tr>
                                <th>{t("table.name")}</th>
                                <th>{t("table.email")}</th>
                                <th>{t("table.status")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {doctors.map((doc) => {
                                const isExtra = extraDoctorIds.has(doc.id) || extraStaffIds.has(doc.id);
                                const extraFee = extraDoctorIds.has(doc.id) ? `+€${EXTRA_DOCTOR_FEE}/mo` : `+€${EXTRA_STAFF_FEE.toFixed(2).replace(".", ",")}/mo`;
                                const scheduledFor = doc.pendingDeletionAt ? new Date(doc.pendingDeletionAt) : null;
                                return (
                                <tr key={doc.id}>
                                    <td>
                                        {doc.firstName} {doc.lastName}
                                        {isExtra && <span className="team-extra-badge">{extraFee}</span>}
                                        {scheduledFor && (
                                            <span className="team-extra-badge team-extra-badge--pending">
                                                {t("settings.scheduledForRemoval", { date: scheduledFor.toLocaleDateString(undefined, { day: "numeric", month: "long" }), defaultValue: "removal scheduled: {{date}}" })}
                                            </span>
                                        )}
                                    </td>
                                    <td>{doc.email}</td>
                                    <td>{USER_ROLES.find(r => r.value === (doc.userRights ?? 2))?.label ?? "—"}</td>
                                    <td className="team-actions">
                                        <button className="tbl-btn" onClick={() => openEdit(doc)}>
                                            {t("form.change")}
                                        </button>
                                        {doc.id !== currentDoctorId && (
                                            scheduledFor ? (
                                                <button className="tbl-btn" onClick={() => onCancelDeletion(doc.id)}>
                                                    {t("settings.cancelScheduledRemoval", { defaultValue: "Cancel removal" })}
                                                </button>
                                            ) : (
                                                <button className="tbl-btn tbl-btn--danger" onClick={() => openDelete(doc)}>
                                                    {t("form.delete")}
                                                </button>
                                            )
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Permissions */}
                    <h3 className="settings-subsection-label">{t("settings.permissionsHeading", { defaultValue: "Berechtigungen" })}</h3>
                    <div className="settings-info-banner settings-info-banner--neutral settings-info-banner--row">
                        <TbInfoCircle size={16} />
                        <div>
                            <p>{t("settings.sharedSettingsNoticeP1")}</p>
                            <p className="settings-info-banner__highlight">{t("settings.sharedSettingsNoticeP2")}</p>
                            <p>{t("settings.sharedSettingsNoticeP3")}</p>
                        </div>
                    </div>

                    <div className="roles-matrix-wrap">
                        <p className="roles-matrix__hint">
                            <TbEye size={15} />
                            <span>{t("settings.rolesTable.hint")}</span>
                        </p>
                        <table className="roles-matrix">
                            <thead>
                                <tr>
                                    <th className="roles-matrix__feat-col">{t("settings.rolesTable.feature")}</th>
                                    {doctors.map((doc) => (
                                        <th key={doc.id} className="roles-matrix__user-col">
                                            {doc.firstName} {doc.lastName}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {PERM_GROUPS.map((group) => (
                                    <Fragment key={group.label}>
                                        <tr className="roles-matrix__group-row">
                                            <td colSpan={1 + doctors.length}>{group.label}</td>
                                        </tr>
                                        {group.perms.map((perm) => (
                                            <tr key={perm}>
                                                <td
                                                    className="roles-matrix__feat-col"
                                                    title={
                                                        perm === "viewAllInvoices" ? t("settings.rolesTable.viewAllInvoicesHint") :
                                                        perm === "recipients" ? t("settings.rolesTable.manageRecipientsHint") :
                                                        perm === "datev"   ? t("settings.rolesTable.datevHint") :
                                                        perm === "createForOthers" ? t("settings.rolesTable.createForOthersHint") :
                                                        perm === "bank"    ? t("settings.rolesTable.bankReconcileHint") :
                                                        perm === "dashboardTasks" ? t("settings.rolesTable.dashboardTasksHint") :
                                                        perm === "pvsSavings" ? t("settings.rolesTable.pvsSavingsHint") :
                                                        perm === "bankDashboard" ? t("settings.rolesTable.bankDashboardHint") :
                                                        perm === "cardQr" ? t("settings.rolesTable.cardQrHint") : undefined
                                                    }
                                                >
                                                    {perm === "viewAllInvoices" && (
                                                        <>
                                                            {t("settings.rolesTable.viewAllInvoices")}
                                                            <span className="roles-matrix__feat-sub">{t("settings.rolesTable.viewAllInvoicesSub")}</span>
                                                        </>
                                                    )}
                                                    {perm === "createForOthers" && (
                                                        <>
                                                            {t("settings.rolesTable.createForOthers")}
                                                            <span className="roles-matrix__feat-sub">{t("settings.rolesTable.createForOthersSub")}</span>
                                                        </>
                                                    )}
                                                    {perm === "recipients" && t("settings.rolesTable.manageRecipients")}
                                                    {perm === "bank"       && t("settings.rolesTable.bankPayments")}
                                                    {perm === "reports"    && t("settings.rolesTable.reports")}
                                                    {perm === "dunning"    && t("settings.rolesTable.dunning")}
                                                    {perm === "datev" && (
                                                        <>
                                                            {t("settings.rolesTable.datev")}
                                                            <span className="roles-matrix__feat-sub">{t("settings.rolesTable.datevSub")}</span>
                                                        </>
                                                    )}
                                                    {perm === "settings"   && t("settings.rolesTable.manageSettings")}
                                                    {perm === "tagsGroupsView" && t("settings.rolesTable.viewTagsGroups")}
                                                    {perm === "tagsGroupsEdit" && t("settings.rolesTable.editTagsGroups")}
                                                    {perm === "dashboardTasks" && (
                                                        <>
                                                            {t("settings.rolesTable.dashboardTasks")}
                                                            <span className="roles-matrix__feat-sub">{t("settings.rolesTable.dashboardTasksSub")}</span>
                                                        </>
                                                    )}
                                                    {perm === "pvsSavings" && (
                                                        <>
                                                            {t("settings.rolesTable.pvsSavings")}
                                                            <span className="roles-matrix__feat-sub">{t("settings.rolesTable.pvsSavingsSub")}</span>
                                                        </>
                                                    )}
                                                    {perm === "bankDashboard" && (
                                                        <>
                                                            {t("settings.rolesTable.bankDashboard")}
                                                            <span className="roles-matrix__feat-sub">{t("settings.rolesTable.bankDashboardSub")}</span>
                                                        </>
                                                    )}
                                                    {perm === "cardQr" && (
                                                        <>
                                                            {t("settings.rolesTable.cardQr")}
                                                            <span className="roles-matrix__feat-sub">{t("settings.rolesTable.cardQrSub")}</span>
                                                        </>
                                                    )}
                                                </td>
                                                {doctors.map((doc) => {
                                                    const isCurrentUser = doc.id === currentDoctorId;
                                                    const perms = getPerms(doc);
                                                    // Doctor and Admin always see (and can edit) only their own
                                                    // invoices — that's fixed behavior (praxisScope's default),
                                                    // not a grant, so this row shows an info label instead of a
                                                    // control for those roles. Patients have no equivalent row at
                                                    // all — every role sees the whole praxis's patients unconditionally.
                                                    // Same for the bank row: a Doctor/Admin always has full access to
                                                    // their own bank account only (backend bankScopeDoctorId) — the
                                                    // None/View/Edit control + doctor dropdown are for staff only.
                                                    const isFixedOwnOnly =
                                                        (perm === "viewAllInvoices" || perm === "bank") &&
                                                        (doc.userRights === 2 || doc.userRights === 3);
                                                    // Staff never own invoices — they always pick the doctor, so
                                                    // "create for other doctors" isn't a choice for them.
                                                    if (perm === "createForOthers" && doc.userRights !== 2 && doc.userRights !== 3) {
                                                        return (
                                                            <td key={doc.id} className="roles-matrix__cell">
                                                                <span className="roles-matrix__fixed-own" title={t("settings.rolesTable.createForOthersStaffHint")}>
                                                                    {t("settings.rolesTable.createForOthersStaff")}
                                                                </span>
                                                            </td>
                                                        );
                                                    }
                                                    if (isFixedOwnOnly) {
                                                        return (
                                                            <td key={doc.id} className="roles-matrix__cell">
                                                                <span
                                                                    className="roles-matrix__fixed-own"
                                                                    title={t(perm === "bank" ? "settings.rolesTable.bankFixedOwnHint" : "settings.rolesTable.viewAllFixedHint")}
                                                                >
                                                                    {t("settings.rolesTable.own")}
                                                                </span>
                                                            </td>
                                                        );
                                                    }
                                                    // MedicalAssistant/Accounting: invoice view + write access are
                                                    // merged into one None/View/Edit control — "Edit" implies "View"
                                                    // is granted too (editing without being able to see would be
                                                    // unusable).
                                                    if (perm === "viewAllInvoices") {
                                                        const editKey = "edit";
                                                        const level: "none" | "view" | "edit" =
                                                            perms[editKey] ? "edit" : perms[perm] ? "view" : "none";
                                                        const disabled = !isViewerAdmin || isCurrentUser;
                                                        // Sets both fields in one state write, not two sequential
                                                        // handlePermChange calls — each of those reads getPerms(doc)
                                                        // fresh, and since setLocalPerms doesn't re-render between
                                                        // them, a second call would silently overwrite the first
                                                        // one's change with stale data instead of merging with it.
                                                        const setLevel = (next: "none" | "view" | "edit") => {
                                                            const current = getPerms(doc);
                                                            const updated = {
                                                                ...current,
                                                                [perm]: next !== "none",
                                                                [editKey]: next === "edit",
                                                            };
                                                            setLocalPerms((prevMap) => new Map(prevMap).set(doc.id, updated));
                                                        };
                                                        return (
                                                            <td key={doc.id} className="roles-matrix__cell">
                                                                <div
                                                                    className="roles-matrix__segmented"
                                                                    role="group"
                                                                    aria-label={`${permLabel[perm]} — ${doc.firstName} ${doc.lastName}`}
                                                                >
                                                                    {(["none", "view", "edit"] as const).map((opt) => (
                                                                        <button
                                                                            key={opt}
                                                                            type="button"
                                                                            className={level === opt ? "active" : ""}
                                                                            disabled={disabled}
                                                                            onClick={() => setLevel(opt)}
                                                                        >
                                                                            {t(`settings.rolesTable.access${opt === "none" ? "None" : opt === "view" ? "View" : "Edit"}`)}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                        );
                                                    }
                                                    // Manage recipients & patients: viewing is unconditional for every
                                                    // role (not shown here at all), so this is a None/Edit/Edit+Delete
                                                    // control instead of a plain checkbox — delete cascades to every
                                                    // doctor's invoices for that patient, so it's a separate, higher
                                                    // tier above plain edit, not a second independent checkbox.
                                                    if (perm === "recipients") {
                                                        const level: "none" | "edit" | "editDelete" =
                                                            perms.recipientsDelete ? "editDelete" : perms.recipients ? "edit" : "none";
                                                        const disabled = !isViewerAdmin || isCurrentUser;
                                                        const setLevel = (next: "none" | "edit" | "editDelete") => {
                                                            const current = getPerms(doc);
                                                            const updated = {
                                                                ...current,
                                                                recipients: next !== "none",
                                                                recipientsDelete: next === "editDelete",
                                                            };
                                                            setLocalPerms((prevMap) => new Map(prevMap).set(doc.id, updated));
                                                        };
                                                        return (
                                                            <td key={doc.id} className="roles-matrix__cell">
                                                                <div
                                                                    className="roles-matrix__segmented"
                                                                    role="group"
                                                                    aria-label={`${permLabel[perm]} — ${doc.firstName} ${doc.lastName}`}
                                                                >
                                                                    {(["none", "edit", "editDelete"] as const).map((opt) => (
                                                                        <button
                                                                            key={opt}
                                                                            type="button"
                                                                            className={level === opt ? "active" : ""}
                                                                            disabled={disabled}
                                                                            onClick={() => setLevel(opt)}
                                                                        >
                                                                            {t(`settings.rolesTable.access${opt === "none" ? "None" : opt === "edit" ? "Edit" : "EditDelete"}`)}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                        );
                                                    }
                                                    // Bank — None/View/Edit, same shape as the invoices row above:
                                                    // "Edit" (reconcile transactions) implies "View" is granted too,
                                                    // since reconciling without being able to see the feed at all
                                                    // would be unusable (requireBank gates the whole page). Connect/
                                                    // disconnect/sync aren't part of this control at all — those stay
                                                    // Doctor/Admin-only and non-delegable (requireBankManage).
                                                    if (perm === "bank") {
                                                        const level: "none" | "view" | "edit" =
                                                            perms.bankReconcile ? "edit" : perms.bank ? "view" : "none";
                                                        const disabled = !isViewerAdmin || isCurrentUser;
                                                        const setLevel = (next: "none" | "view" | "edit") => {
                                                            const current = getPerms(doc);
                                                            const updated = {
                                                                ...current,
                                                                bank: next !== "none",
                                                                bankReconcile: next === "edit",
                                                            };
                                                            setLocalPerms((prevMap) => new Map(prevMap).set(doc.id, updated));
                                                        };
                                                        const setDoctorScope = (next: string) => {
                                                            const current = getPerms(doc);
                                                            setLocalPerms((prevMap) => new Map(prevMap).set(doc.id, { ...current, bankDoctorScope: next }));
                                                        };
                                                        return (
                                                            <td key={doc.id} className="roles-matrix__cell">
                                                                <div
                                                                    className="roles-matrix__segmented"
                                                                    role="group"
                                                                    aria-label={`${permLabel[perm]} — ${doc.firstName} ${doc.lastName}`}
                                                                    title={t("settings.rolesTable.bankReconcileHint")}
                                                                >
                                                                    {(["none", "view", "edit"] as const).map((opt) => (
                                                                        <button
                                                                            key={opt}
                                                                            type="button"
                                                                            className={level === opt ? "active" : ""}
                                                                            disabled={disabled}
                                                                            onClick={() => setLevel(opt)}
                                                                        >
                                                                            {t(`settings.rolesTable.access${opt === "none" ? "None" : opt === "view" ? "View" : "Edit"}`)}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                                {/* Which doctor's connected account this grant covers — only meaningful once
                                                                    access is granted at all, and only worth showing when there's more than one
                                                                    Doctor/Admin whose account could be connected in the first place. */}
                                                                {level !== "none" && bankScopeDoctors.length > 1 && (
                                                                    <select
                                                                        className="roles-matrix__bank-scope"
                                                                        value={perms.bankDoctorScope}
                                                                        disabled={disabled}
                                                                        aria-label={`${t("settings.rolesTable.bankDoctorScope")} — ${doc.firstName} ${doc.lastName}`}
                                                                        onChange={(e) => setDoctorScope(e.target.value)}
                                                                    >
                                                                        <option value="all">{t("settings.rolesTable.bankDoctorScopeAll")}</option>
                                                                        {bankScopeDoctors.map((bd) => (
                                                                            <option key={bd.id} value={bd.id}>{bd.firstName} {bd.lastName}</option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                            </td>
                                                        );
                                                    }
                                                    return (
                                                        <td key={doc.id} className="roles-matrix__cell">
                                                            <input
                                                                type="checkbox"
                                                                checked={perms[perm]}
                                                                disabled={
                                                                    !isViewerAdmin ||
                                                                    isCurrentUser ||
                                                                    ((perm === "reports" || perm === "dunning" || perm === "datev") && !perms.settings) ||
                                                                    (perm === "tagsGroupsEdit" && !perms.tagsGroupsView)
                                                                }
                                                                onChange={(e) => handlePermChange(doc, perm, e.target.checked)}
                                                                aria-label={`${permLabel[perm]} — ${doc.firstName} ${doc.lastName}`}
                                                                title={
                                                                    perm === "datev"   ? t("settings.rolesTable.datevHint") :
                                                                    perm === "createForOthers" ? t("settings.rolesTable.createForOthersHint") :
                                                                    perm === "dashboardTasks" ? t("settings.rolesTable.dashboardTasksHint") :
                                                                    perm === "pvsSavings" ? t("settings.rolesTable.pvsSavingsHint") :
                                                                    perm === "bankDashboard" ? t("settings.rolesTable.bankDashboardHint") :
                                                                    perm === "cardQr" ? t("settings.rolesTable.cardQrHint") : undefined
                                                                }
                                                            />
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                        {saveButtonPortalTarget
                            ? createPortal(saveButton, saveButtonPortalTarget)
                            : <div className="roles-matrix-save">{saveButton}</div>
                        }
                    </div>
                </>
            )}

            {addOpen && (
                <div className="create-modal-overlay" role="presentation" onClick={closeAdd}>
                    <div
                        ref={addDialogRef}
                        className="create-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={addTitleId}
                        tabIndex={-1}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="create-modal__title" id={addTitleId}>{t("settings.newMember")}</h2>
                        <DoctorForm f={form} setF={setForm} onSubmit={handleAdd} onClose={closeAdd} submitting={adding} error={addError} submitLabel={t("settings.addMemberBtn")} isEdit={false} specialties={specialties} costNote={getAddCostNote()} />
                    </div>
                </div>
            )}

            {editDoc && (
                <div className="create-modal-overlay" role="presentation" onClick={closeEdit}>
                    <div
                        ref={editDialogRef}
                        className="create-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={editTitleId}
                        tabIndex={-1}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="create-modal__title" id={editTitleId}>{t("settings.editMember")}</h2>
                        <DoctorForm f={editForm} setF={setEditForm} onSubmit={handleEdit} onClose={closeEdit} submitting={editing} error={editError} submitLabel={t("settings.saveChangesBtn")} isEdit={true} specialties={specialties} lockAdminRole={editDoc?.userRights === 3 && adminCount <= 1} />
                    </div>
                </div>
            )}

            {deleteTarget && (
                <Popup
                    title={t("settings.deleteMemberTitle", { name: `${deleteTarget.firstName} ${deleteTarget.lastName}`, defaultValue: "{{name}} entfernen?" })}
                    message={
                        <div className="team-delete-confirm">
                            <label className="team-delete-confirm__option">
                                <input
                                    type="radio"
                                    name="delete-mode"
                                    checked={deleteMode === "immediate"}
                                    onChange={() => setDeleteMode("immediate")}
                                />
                                <span>
                                    <strong>{t("settings.deleteModeImmediate", { defaultValue: "Delete immediately" })}</strong>
                                    <br />
                                    {t("settings.deleteModeImmediateDesc", { defaultValue: "Access is revoked right away. To undo this, you'll need to add them again as a new member." })}
                                </span>
                            </label>

                            <label className="team-delete-confirm__option">
                                <input
                                    type="radio"
                                    name="delete-mode"
                                    checked={deleteMode === "endOfMonth"}
                                    onChange={() => setDeleteMode("endOfMonth")}
                                />
                                <span>
                                    <strong>{t("settings.deleteModeEndOfMonth", { defaultValue: "Delete at end of month" })}</strong>
                                    <br />
                                    {t("settings.deleteModeEndOfMonthDesc", { defaultValue: "They keep full access until then. Removal happens automatically on the 1st of next month — you can cancel it anytime before that." })}
                                </span>
                            </label>

                            {isExtraMember(deleteTarget) && (
                                <>
                                    <div className="settings-info-banner settings-info-banner--warn">
                                        <TbAlertCircle className="settings-info-banner__icon" />
                                        <span>{t("settings.deleteMemberBillingNote", { fee: feeForMember(deleteTarget), defaultValue: "This month's {{fee}}/month charge for this seat still applies, whichever option you choose — the reduction only appears on your invoice from the 1st of next month." })}</span>
                                    </div>
                                    <label className="team-delete-confirm__ack">
                                        <input
                                            type="checkbox"
                                            checked={deleteAck}
                                            onChange={(e) => setDeleteAck(e.target.checked)}
                                        />
                                        <span>{t("settings.deleteAckLabel", { fee: feeForMember(deleteTarget), name: `${deleteTarget.firstName} ${deleteTarget.lastName}`, defaultValue: "I understand that {{fee}}/month will still be charged for {{name}} this month." })}</span>
                                    </label>
                                </>
                            )}
                            {deleteError && <p className="create-modal__error">{deleteError}</p>}
                        </div>
                    }
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: closeDelete, disabled: deleting },
                        {
                            label: deleting ? t("settings.saving") : t("form.delete"),
                            variant: "danger",
                            disabled: deleting || (isExtraMember(deleteTarget) && !deleteAck),
                            onClick: confirmDelete,
                        },
                    ]}
                    onClose={closeDelete}
                />
            )}

        </Section>
    );
}
